import { useDescope, useUser } from '@descope/react-sdk';
import { authStore } from '../utils/authStore';
import { useEffect, useState } from 'react';
export const useAuth = () => {
    const { user, isUserLoading } = useUser();
    const { logout, getSessionToken, getRefreshToken, refresh } = useDescope();
    const [isRestoringSession, setIsRestoringSession] = useState(false);
    // Debug authentication state
    useEffect(() => {
        const sessionToken = getSessionToken();
        const refreshToken = getRefreshToken();
        console.log('🔐 Auth State Debug:', {
            user,
            isUserLoading,
            isAuthenticated: !!user,
            hasSessionToken: !!sessionToken,
            hasRefreshToken: !!refreshToken,
            hasPersistentAuth: authStore.hasValidAuthData(),
            storedUser: authStore.getUser(),
        });
        // Save tokens to persistent storage when they change
        if (sessionToken && user) {
            authStore.saveAuthData({
                user,
                sessionToken,
                refreshToken,
            });
            console.log('🔐 Tokens saved to persistent storage');
        }
    }, [user, isUserLoading, getSessionToken, getRefreshToken]);
    // Restore session from stored tokens
    useEffect(() => {
        const restoreSession = async () => {
            const sessionToken = getSessionToken();
            const refreshToken = getRefreshToken();
            const storedUser = authStore.getUser();
            // If we have tokens but no user, try to restore the session
            if ((sessionToken || refreshToken) && !user && !isUserLoading && !isRestoringSession) {
                console.log('🔐 Attempting to restore session from stored tokens...');
                setIsRestoringSession(true);
                try {
                    if (refreshToken) {
                        // Try to refresh the session
                        await refresh();
                        console.log('🔐 Session restored successfully via refresh');
                    }
                    else if (storedUser) {
                        // If we have stored user data, we can at least show that
                        console.log('🔐 Using stored user data as fallback');
                    }
                }
                catch (error) {
                    console.error('🔐 Failed to restore session:', error);
                    // Clear invalid tokens
                    authStore.clearAuthData();
                }
                finally {
                    setIsRestoringSession(false);
                }
            }
        };
        restoreSession();
    }, [user, isUserLoading, getSessionToken, getRefreshToken, refresh, isRestoringSession]);
    // Enhanced logout that also clears persistent storage
    const enhancedLogout = async () => {
        try {
            await logout();
            authStore.clearAuthData();
            console.log('🔐 User logged out and auth data cleared');
        }
        catch (error) {
            console.error('Logout error:', error);
        }
    };
    // Check if user has persistent auth data
    const hasPersistentAuth = authStore.hasValidAuthData();
    return {
        user,
        isAuthenticated: !!user,
        isLoading: isUserLoading || isRestoringSession,
        logout: enhancedLogout,
        hasPersistentAuth,
        authStore,
        sessionToken: getSessionToken(),
        refreshToken: getRefreshToken(),
    };
};
